import { useNavigate } from 'react-router-dom';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GraduationCap,
  BookOpen,
  Briefcase,
  CheckCircle2,
  Check,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap-config';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import './conversion-studio.css';
import type { AudienceId } from '@/types';

interface AudienceGroup {
  id: AudienceId;
  icon: typeof GraduationCap;
  label: string;
  badge: string;
  title: string;
  description: string;
  features: string[];
  mockup: {
    tag: string;
    title: string;
    items: string[];
  };
}

const faqsVi = [
  {
    question: 'Lamslide hoạt động như thế nào?',
    answer: 'Bạn chỉ cần nhập một câu prompt hoặc tải lên tài liệu Word, PDF hay dàn ý thô. Trí tuệ nhân tạo của Lamslide sẽ tự động phân tích cấu trúc luận điểm, phân bổ thông tin hợp lý vào từng trang, áp dụng quy tắc tỷ lệ vàng 16:9 và xuất ra tệp slide chuyên nghiệp chỉ trong 30 giây.',
  },
  {
    question: 'Tệp xuất ra có thực sự tương thích 100% với Microsoft PowerPoint không?',
    answer: 'Có, tuyệt đối 100%. Lamslide không xuất ra ảnh tĩnh chụp màn hình. Toàn bộ hình khối, chữ, mũi tên và biểu đồ đều là vector gốc chuẩn định dạng .PPTX, bạn có thể mở bằng Microsoft PowerPoint, Google Slides hoặc Keynote để chỉnh sửa chi tiết mọi lúc.',
  },
  {
    question: 'Lamslide hỗ trợ tiếng Việt và các chuẩn giáo dục Việt Nam ra sao?',
    answer: 'Lamslide được tối ưu hóa chuyên sâu cho bộ font tiếng Việt (không bao giờ bị lỗi dấu hay vỡ dòng). Kho mẫu được thiết kế riêng theo chương trình GDPT 2018 của Bộ Giáo dục & Đào tạo, bài giảng tương tác và slide bảo vệ đồ án/KLTN của các trường đại học tại Việt Nam.',
  },
  {
    question: 'Làm thế nào để Sinh viên và Giáo viên nhận ưu đãi giảm giá 60%?',
    answer: 'Chỉ cần đăng ký tài khoản bằng email trường học (.edu, .edu.vn) hoặc tải lên ảnh thẻ học sinh, sinh viên, giảng viên trong phần cài đặt. Hệ thống sẽ tự động kích hoạt mức giá ưu đãi 49.000đ/tháng ngay lập tức.',
  },
  {
    question: 'Dữ liệu tài liệu và bài giảng của tôi có được bảo mật không?',
    answer: 'Có. Toàn bộ tài liệu bạn tải lên chỉ được xử lý trong phiên làm việc để tạo slide và không bao giờ được sử dụng để huấn luyện mô hình AI công khai. Dữ liệu được mã hóa đầu cuối chuẩn AES-256.',
  },
  {
    question: 'Tôi có thể nhúng biểu đồ Excel hoặc số liệu tài chính vào slide không?',
    answer: 'Hoàn toàn được. Bạn có thể copy bảng số liệu hoặc dán bảng Excel vào prompt, AI sẽ tự động dựng biểu đồ cột, biểu đồ tròn, biểu đồ đường hoặc ma trận tài chính trực quan tương ứng.',
  },
];

const faqsEn = [
  {
    question: 'How does Lamslide work?',
    answer: 'Simply enter a text prompt or upload a Word document, PDF, or rough outline. Lamslide’s AI automatically structures key arguments, aligns 16:9 layouts, and renders presentation-ready slides in 30 seconds.',
  },
  {
    question: 'Are exported files 100% compatible with Microsoft PowerPoint?',
    answer: 'Yes, completely. Lamslide generates native editable vector shapes, text frames, and charts in .PPTX format. Open and edit them smoothly in Microsoft PowerPoint, Google Slides, or Keynote.',
  },
  {
    question: 'Does Lamslide support multiple languages and pedagogical formats?',
    answer: 'Yes. Lamslide supports international typography without font clipping or line breaks, and includes standard templates for academic theses, school curricula, and corporate presentations.',
  },
  {
    question: 'How can Students and Educators claim the 60% discount?',
    answer: 'Sign up using your school email (.edu) or verify your student/faculty ID card in account settings to activate instant educational pricing.',
  },
  {
    question: 'Is my document and presentation data secure?',
    answer: 'Yes. Uploaded files are strictly processed within your isolated session to generate slides and are never used to train public models. All data is protected by enterprise-grade AES-256 encryption.',
  },
  {
    question: 'Can I import financial tables or Excel data directly into slides?',
    answer: 'Absolutely. Paste tabular data or raw Excel sheets into your prompt, and AI will automatically construct corresponding column charts, line graphs, and financial summary tables.',
  },
];

export function ConversionEcosystemSection() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const audienceGroups = useMemo(() => (['teacher', 'student', 'office'] as AudienceId[]).map((id, index) => ({
    ...(t(`audience.personas.${id}`, { returnObjects: true }) as Omit<AudienceGroup, 'id' | 'icon'>),
    id, icon: [GraduationCap, BookOpen, Briefcase][index],
  })), [t, isEn]);
  const faqs = useMemo(() => (isEn ? faqsEn : faqsVi), [isEn]);

  const [activeAudience, setActiveAudience] = useState<AudienceId>('teacher');
  const [isAnnual, setIsAnnual] = useState(false);
  const [openFAQIdx, setOpenFAQIdx] = useState<number | null>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const audiencePanelRef = useRef<HTMLDivElement>(null);

  const handleAudienceChange = (id: AudienceId) => setActiveAudience(id);
  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo(audiencePanelRef.current, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.45 });
    });
    return () => media.revert();
  }, { scope: containerRef, dependencies: [activeAudience, isEn], revertOnUpdate: true });

  // Keep FAQ content visible; only decorative entrances depend on scroll.
  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
    gsap.from('.v2-pricing-card', {
      scrollTrigger: {
        trigger: '.v2-pricing-grid',
        start: 'top 85%',
      },
      y: 40,
      autoAlpha: 0,
      duration: 0.8,
      stagger: 0.15,
      ease: 'power2.out',
    });

    gsap.from('.v2-cta-box', {
      scrollTrigger: {
        trigger: '.v2-cta-box',
        start: 'top 85%',
      },
      scale: 0.95,
      y: 35,
      autoAlpha: 0,
      duration: 0.85,
      ease: 'power3.out',
    });
    });
    return () => media.revert();
  }, { scope: containerRef, dependencies: [isEn], revertOnUpdate: true });

  useEffect(() => {
    const frame = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(frame);
  }, [openFAQIdx, isEn]);

  const currentAudience = audienceGroups.find((a) => a.id === activeAudience) ?? audienceGroups[0]!;

  return (
    <section
      ref={containerRef}
      className="py-24 md:py-36 bg-background relative overflow-hidden"
    >
      <div className="container mx-auto px-4 max-w-6xl">
        {/* ================= 1. AUDIENCE PERSONAS ================= */}
        <div id="audience" className="solution-studio mb-32">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="font-mono text-xs text-brand font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-brand inline-block" />
              <span>[{t('audience.act')}]</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-foreground">
              {t('audience.sectionTitle')}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-4 leading-relaxed">
              {t('audience.sectionSubtitle')}
            </p>

            {/* Audience Tabs Row */}
            <div className="flex justify-center mt-8">
              <div className="inline-flex p-1.5 bg-muted/60 rounded-btn border border-border">
                {audienceGroups.map((g) => {
                  const Icon = g.icon;
                  const isSelected = g.id === activeAudience;
                  return (
                    <button
                      key={g.id}
                      onClick={() => handleAudienceChange(g.id)}
                      aria-pressed={isSelected}
                      className={cn(
                        'flex items-center gap-2 px-5 py-2.5 rounded text-xs font-mono font-semibold transition-all cursor-pointer',
                        isSelected
                          ? 'bg-foreground text-background shadow-subtle'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{g.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Audience Dynamic Content Panel */}
          <div ref={audiencePanelRef} className="solution-panel grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7 rounded-card border border-border bg-card p-8 flex flex-col justify-between text-left shadow-subtle">
              <div>
                <span className="text-xs font-mono font-bold text-brand block mb-2">
                  // {currentAudience.badge}
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
                  {currentAudience.title}
                </h3>
                <p className="text-muted-foreground text-sm mt-3 leading-relaxed">
                  {currentAudience.description}
                </p>

                <ul className="space-y-3.5 pt-6 border-t border-border/60 mt-6">
                  {currentAudience.features.map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-xs sm:text-sm text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-8 mt-6 border-t border-border/60 flex items-center gap-3">
                <Button onClick={() => navigate('/coming-soon')} variant="solid" size="md" className="font-mono text-xs font-bold">
                  {t('audience.cta')} {currentAudience.label}
                  <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
                <Button onClick={() => navigate('/coming-soon')} variant="outline" size="md" className="font-mono text-xs">
                  {t('audience.sample')}
                </Button>
              </div>
            </div>

            <div className="solution-preview lg:col-span-5 rounded-card border border-border bg-card p-6 sm:p-8 flex flex-col justify-between text-left shadow-subtle">
              <div>
                <div className="flex items-center justify-between font-mono text-xs text-muted-foreground pb-4 border-b border-border/60">
                  <span className="text-brand font-bold">{currentAudience.mockup.tag}</span>
                  <span className="text-emerald-400 font-semibold">100% PPTX</span>
                </div>

                <h4 className="text-lg font-bold text-foreground mt-5">
                  {currentAudience.mockup.title}
                </h4>

                <div className="mt-5 space-y-3">
                  {currentAudience.mockup.items.map((item, idx) => (
                    <div key={idx} className="p-3.5 rounded bg-muted/40 border border-border text-xs text-foreground font-mono">
                      <span className="solution-item-number">0{idx + 1}</span>{item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-border/60 flex items-center justify-between font-mono text-[11px] text-muted-foreground">
                <span>{t('audience.renderTime')}</span>
                <span className="text-foreground font-semibold">{t('audience.autoAlign')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ================= 2. PRICING TABLE ================= */}
        <div id="pricing" className="pricing-studio pt-16 pb-32 border-t border-border/80">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="font-mono text-xs text-brand font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-brand inline-block" />
              <span>[{t('pricing.tag')}]</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-foreground">
              {t('pricing.sectionTitle')}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-4 leading-relaxed">
              {t('pricing.sectionSubtitle')}
            </p>

            <div className="inline-flex items-center p-1 bg-muted/60 rounded-btn border border-border mt-8">
              <button
                onClick={() => setIsAnnual(false)}
                aria-pressed={!isAnnual}
                className={cn(
                  'px-4 py-1.5 rounded text-xs font-mono font-semibold transition-all cursor-pointer',
                  !isAnnual
                    ? 'bg-foreground text-background shadow-subtle'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                aria-pressed={isAnnual}
                className={cn(
                  'px-4 py-1.5 rounded text-xs font-mono font-semibold transition-all cursor-pointer flex items-center gap-1.5',
                  isAnnual
                    ? 'bg-foreground text-background shadow-subtle'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <span>{t('pricing.annual')}</span>
                <span className="px-1.5 py-0.2 rounded bg-emerald-500 text-white text-[9px] font-bold">
                  {t('pricing.save')}
                </span>
              </button>
            </div>
          </div>

          <p className="pricing-billing-note">{t(isAnnual ? 'pricing.annualNote' : 'pricing.monthlyNote')}</p>
          <div className="v2-pricing-grid grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {/* Free */}
            <div className="v2-pricing-card rounded-card border border-border bg-card p-8 flex flex-col justify-between text-left">
              <div>
                <div className="flex items-center justify-between font-mono text-xs text-muted-foreground pb-4 border-b border-border">
                  <span>{t('pricing.free.tag')}</span>
                  <span>{t('pricing.free.badge')}</span>
                </div>
                <div className="my-6">
                  <h3 className="text-xl font-bold text-foreground">{t('pricing.free.name')}</h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-4xl font-extrabold text-foreground font-mono">{t('pricing.free.price')}</span>
                    <span className="text-xs text-muted-foreground font-mono">{t('pricing.free.period')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t('pricing.free.desc')}</p>
                </div>
                <ul className="space-y-3 pt-4 border-t border-border/60 text-xs font-medium text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-foreground shrink-0" />
                    <span>{t('pricing.free.f1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-foreground shrink-0" />
                    <span>{t('pricing.free.f2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-foreground shrink-0" />
                    <span>{t('pricing.free.f3')}</span>
                  </li>
                </ul>
              </div>
              <Button onClick={() => navigate('/coming-soon')} variant="outline" size="md" className="w-full mt-8 font-mono text-xs">
                {t('pricing.free.cta')}
              </Button>
            </div>

            {/* Student & Educator */}
            <div className="v2-pricing-card pricing-featured rounded-card border-2 border-brand bg-card p-8 flex flex-col justify-between text-left shadow-elevated relative">
              <div className="absolute -top-3.5 right-6 px-3 py-0.5 rounded-pill bg-brand text-white font-mono text-[10px] font-bold tracking-wider">
                {t('pricing.student.discountBadge')}
              </div>
              <div>
                <div className="flex items-center justify-between font-mono text-xs text-brand pb-4 border-b border-border font-bold">
                  <span>{t('pricing.student.tag')}</span>
                  <span>{t('pricing.student.badge')}</span>
                </div>
                <div className="my-6">
                  <h3 className="text-xl font-bold text-foreground">{t('pricing.student.name')}</h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-4xl font-extrabold text-foreground font-mono">
                      {isAnnual ? t('pricing.student.priceAnnual') : t('pricing.student.priceMonthly')}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">{t('pricing.student.period')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t('pricing.student.desc')}</p>
                </div>
                <ul className="space-y-3 pt-4 border-t border-border/60 text-xs font-medium text-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-brand shrink-0" />
                    <span className="font-bold">{t('pricing.student.f1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-brand shrink-0" />
                    <span>{t('pricing.student.f2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-brand shrink-0" />
                    <span className="font-bold text-emerald-500">{t('pricing.student.f3')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-brand shrink-0" />
                    <span>{t('pricing.student.f4')}</span>
                  </li>
                </ul>
              </div>
              <Button onClick={() => navigate('/coming-soon')} variant="solid" size="md" className="w-full mt-8 font-mono text-xs font-bold">
                {t('pricing.student.cta')}
                <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </div>

            {/* Pro */}
            <div className="v2-pricing-card rounded-card border border-border bg-card p-8 flex flex-col justify-between text-left">
              <div>
                <div className="flex items-center justify-between font-mono text-xs text-muted-foreground pb-4 border-b border-border">
                  <span>{t('pricing.pro.tag')}</span>
                  <span>{t('pricing.pro.badge')}</span>
                </div>
                <div className="my-6">
                  <h3 className="text-xl font-bold text-foreground">{t('pricing.pro.name')}</h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-4xl font-extrabold text-foreground font-mono">
                      {isAnnual ? t('pricing.pro.priceAnnual') : t('pricing.pro.priceMonthly')}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">{t('pricing.pro.period')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{t('pricing.pro.desc')}</p>
                </div>
                <ul className="space-y-3 pt-4 border-t border-border/60 text-xs font-medium text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-foreground shrink-0" />
                    <span>{t('pricing.pro.f1')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-foreground shrink-0" />
                    <span>{t('pricing.pro.f2')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-foreground shrink-0" />
                    <span>{t('pricing.pro.f3')}</span>
                  </li>
                </ul>
              </div>
              <Button onClick={() => navigate('/coming-soon')} variant="outline" size="md" className="w-full mt-8 font-mono text-xs">
                {t('pricing.pro.cta')}
              </Button>
            </div>
          </div>
        </div>

        {/* ================= 3. FAQ ACCORDION ================= */}
        <div id="faq" className="faq-studio pt-16 pb-32 border-t border-border/80">
          <div className="faq-intro">
            <div className="font-mono text-xs text-brand font-bold uppercase tracking-widest inline-flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-brand inline-block" />
              <span>[{t('faq.tag')}]</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
              {t('faq.sectionTitle')}
            </h2>
            <p className="text-sm text-muted-foreground mt-3">
              {t('faq.sectionSubtitle')}
            </p>
            <Button onClick={() => navigate('/coming-soon')} variant="outline" size="md" className="mt-6">{t('faq.contact')}<ArrowRight size={14} className="ml-2" aria-hidden="true" /></Button>
          </div>

          <div className="v2-faq-grid space-y-3 text-left">
            {faqs.map((faq, idx) => {
              const isOpen = openFAQIdx === idx;
              return (
                <div
                  key={idx}
                  className={cn(
                    'v2-faq-card rounded-card border transition-colors duration-200 overflow-hidden',
                    isOpen
                      ? 'border-foreground/30 bg-card shadow-subtle'
                      : 'border-border bg-card/60 hover:border-border/80'
                  )}
                >
                  <button
                    onClick={() => setOpenFAQIdx(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${idx}`}
                    id={`faq-question-${idx}`}
                    className="w-full p-5 sm:p-6 flex items-center justify-between gap-4 text-left cursor-pointer select-none"
                  >
                    <span className="faq-number" aria-hidden="true">{String(idx + 1).padStart(2, '0')}</span>
                    <span className="faq-question text-base sm:text-lg font-bold text-foreground tracking-tight">
                      {faq.question}
                    </span>
                    <div
                      className={cn(
                        'w-7 h-7 rounded border border-border flex items-center justify-center shrink-0 transition-transform duration-200 text-muted-foreground',
                        isOpen && 'rotate-180 text-foreground border-foreground/40 bg-muted'
                      )}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>
                  {isOpen && (
                    <div id={`faq-answer-${idx}`} role="region" aria-labelledby={`faq-question-${idx}`} className="faq-answer px-5 sm:px-6 pb-6 pt-1 border-t border-border/40 text-sm text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ================= 4. FINAL CLOSING CTA BANNER ================= */}
        <div className="v2-cta-box closing-studio">
          <div className="closing-copy">
            <p className="closing-eyebrow">{t('cta.tag')}</p>
            <h2>{t('cta.title')}<span>{t('cta.accent')}</span></h2>
            <p className="closing-description">{t('cta.subtitle')}</p>
            <div className="closing-actions">
              <Button onClick={() => navigate('/coming-soon')} variant="solid" size="lg">
                {t('cta.primary')}<ArrowRight className="w-4 h-4 ml-3" aria-hidden="true" />
              </Button>
              <Button onClick={() => navigate('/coming-soon')} variant="ghost" size="lg">{t('cta.secondary')}</Button>
            </div>
            <p className="closing-note">{t('cta.note')}</p>
          </div>
          <div className="closing-art" aria-hidden="true">
            <div className="closing-orbit" />
            <div className="closing-sheet closing-sheet-back" />
            <div className="closing-sheet closing-sheet-middle" />
            <div className="closing-sheet closing-sheet-front">
              <span className="closing-sheet-label">LÀM SLIDE / 01</span>
              <strong>{t('cta.previewTitle')}<em>{t('cta.previewAccent')}</em></strong>
              <svg viewBox="0 0 300 120" fill="none"><path d="M0 100C40 100 60 50 100 65S160 10 200 35S270 5 300 5" stroke="currentColor" strokeWidth="3" /><path d="M0 115H300M0 65H300M0 15H300" stroke="currentColor" opacity=".12" /></svg>
              <span className="closing-sheet-footer">{t('cta.previewFooter')} <span>16:9</span></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ConversionEcosystemSection;

